import React, { useState, useEffect, useRef } from "react";
import { Trash2, Edit2 } from "lucide-react";
import { ReactComponent as Plus } from "../../../../assets/icons/svg/plus.svg";
import { ReactComponent as PlusColor } from "../../../../assets/icons/svg/plus-color.svg";
import { ReactComponent as InformationIcon } from "../../../../assets/icons/svg/information-circle-icon.svg";
import ItemListPopup from "../../../ui/ItemListPopup";
import DeletePopup from "../../../ui/DeletePopup";
import { useTags } from "../../../../context/TagContext";

interface TagManagementPopupProps {
  onClose: () => void;
  showToast: (type: string, title: string, message: string) => void;
}

const TagManagementPopup: React.FC<TagManagementPopupProps> = ({
  onClose,
  showToast,
}) => {
  const { availableTags, addTag, updateTag, deleteTag } = useTags();

  const [tempTagName, setTempTagName] = useState("");
  const [showAddTagInput, setShowAddTagInput] = useState(false);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editingTagValue, setEditingTagValue] = useState("");
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (availableTags.length === 0) {
      setShowAddTagInput(true);
    }
  }, [availableTags]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close if delete popup is showing or if clicking on delete popup
      if (showDeletePopup) {
        return;
      }

      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, showDeletePopup]);

  const handleAddNewTag = () => {
    if (availableTags.length >= 5) {
      showToast("error", "Maximum Limit", "You can only create up to 5 tags");
      return;
    }
    setTempTagName("");
    setShowAddTagInput(true);
    setEditingTagId(null);
  };

  const handleSaveTag = () => {
    if (!tempTagName.trim()) {
      showToast("error", "Invalid Input", "Tag name cannot be empty");
      return;
    }

    const success = addTag(tempTagName.trim());
    if (!success) {
      const tagExists = availableTags.some(
        (tag) => tag.name.toLowerCase() === tempTagName.toLowerCase()
      );

      if (tagExists) {
        showToast(
          "error",
          "Duplicate Tag",
          "A tag with this name already exists"
        );
      } else if (availableTags.length >= 5) {
        showToast("error", "Maximum Limit", "You can only create up to 5 tags");
      }
      return;
    }

    showToast("success", "Tag Created", "Tag has been created successfully");
    setTempTagName("");
    setShowAddTagInput(false);
  };

  const handleEditTag = (tagId: string) => {
    const tag = availableTags.find((t) => t.id === tagId);
    if (tag) {
      setEditingTagId(tagId);
      setEditingTagValue(tag.name);
    }
  };

  const handleSaveEditTag = () => {
    if (!editingTagValue.trim()) {
      showToast("error", "Invalid Input", "Tag name cannot be empty");
      return;
    }

    if (editingTagId) {
      const success = updateTag(editingTagId, editingTagValue.trim());
      if (!success) {
        showToast(
          "error",
          "Duplicate Tag",
          "A tag with this name already exists"
        );
        return;
      }

      showToast("success", "Tag Updated", "Tag has been updated successfully");
      setEditingTagId(null);
      setEditingTagValue("");
    }
  };

  const handleCancelEdit = () => {
    setEditingTagId(null);
    setEditingTagValue("");
  };

  const confirmDeleteTag = (tagId: string) => {
    // Find the tag to get its name for better user experience
    const tagToDeleteObj = availableTags.find((tag) => tag.id === tagId);
    if (tagToDeleteObj) {
      setTagToDelete(tagId);
      setShowDeletePopup(true);
    }
  };

  const handleDeleteConfirmed = () => {
    if (!tagToDelete) {
      console.log("TagManagement: No tag to delete");
      return;
    }

    console.log(
      "TagManagement: Attempting to delete tag with ID:",
      tagToDelete
    );

    // Get tag name before deletion for toast message
    const tagToDeleteObj = availableTags.find((tag) => tag.id === tagToDelete);
    const tagName = tagToDeleteObj?.name || "Tag";

    console.log("TagManagement: Tag to delete:", tagToDeleteObj);

    if (!tagToDeleteObj) {
      console.error("TagManagement: Tag not found in availableTags!");
      showToast("error", "Delete Failed", "Tag not found");
      setShowDeletePopup(false);
      setTagToDelete(null);
      return;
    }

    try {
      // Call delete function from context
      console.log("TagManagement: Calling deleteTag function...");
      deleteTag(tagToDelete);
      console.log("TagManagement: deleteTag function called successfully");

      // Clean up popup state first
      setShowDeletePopup(false);
      setTagToDelete(null);
      setIsDeleting(false);

      // Clear any editing states
      setEditingTagId(null);
      setEditingTagValue("");

      // Show success message after cleanup
      setTimeout(() => {
        showToast(
          "success",
          "Tag Deleted",
          `"${tagName}" has been permanently deleted`
        );
      }, 100);
    } catch (error) {
      console.error("TagManagement: Error deleting tag:", error);
      showToast(
        "error",
        "Delete Failed",
        "An error occurred while deleting the tag"
      );
      setShowDeletePopup(false);
      setTagToDelete(null);
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    if (!isDeleting) {
      setShowDeletePopup(false);
      setTagToDelete(null);
    }
  };

  // Get the tag name for delete confirmation message
  const tagToDeleteName = tagToDelete
    ? availableTags.find((tag) => tag.id === tagToDelete)?.name || "this tag"
    : "this tag";

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50" />
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          ref={popupRef}
          className={`${
            showDeletePopup ? "pointer-events-none opacity-20" : ""
          }`}
        >
          <ItemListPopup title="Tags" onClose={onClose}>
            <div className="py-3 px-2 space-y-4">
              <div className="flex flex-row items-center gap-2 pl-4 text-xs text-[#0BA6C7] bg-[#0BA6C71A] p-2 rounded-[10px]">
                <InformationIcon />
                Max 5 tags allowed.
              </div>

              {availableTags.map((tag) => (
                <div key={tag.id}>
                  {editingTagId === tag.id ? (
                    <div className="border p-4 rounded-[8px] bg-[#FAFAFA] space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">Tag Name</span>
                        <button
                          onClick={() => confirmDeleteTag(tag.id)}
                          className="text-red-500 hover:text-red-600 transition-colors"
                          title="Delete tag"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <input
                        value={editingTagValue}
                        onChange={(e) => setEditingTagValue(e.target.value)}
                        placeholder="Enter tag name"
                        className="w-full px-2 py-2 bg-white border-[0.5px] border-[#D4D4D4] rounded-[8px] text-sm focus:outline-none focus:border-[#0BA6C7]"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSaveEditTag();
                          } else if (e.key === "Escape") {
                            handleCancelEdit();
                          }
                        }}
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={handleCancelEdit}
                          className="px-4 py-0.5 border-[0.5px] border-gray-300 text-gray-600 rounded-[8px] text-sm hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveEditTag}
                          disabled={!editingTagValue.trim()}
                          className={`px-4 py-0.5 border-[0.5px] text-white rounded-[8px] text-sm transition-colors ${
                            editingTagValue.trim()
                              ? "bg-[#0BA6C7] border-[#0BA6C7] hover:bg-[#0BA6C7]/90"
                              : "bg-[#808191] border-[#808191] cursor-not-allowed"
                          }`}
                        >
                          Update
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-[8px] hover:bg-gray-100 transition-colors">
                      <span className="text-sm font-medium">{tag.name}</span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditTag(tag.id)}
                          className="text-[#0BA6C7] hover:text-[#0BA6C7]/80 transition-colors"
                          title="Edit tag"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => confirmDeleteTag(tag.id)}
                          className="text-red-500 hover:text-red-600 transition-colors"
                          title="Delete tag permanently"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {(showAddTagInput || availableTags.length === 0) && (
                <div className="border p-4 rounded-[8px] bg-[#FAFAFA] space-y-3">
                  <label className="text-sm font-medium">Tag Name</label>
                  <input
                    value={tempTagName}
                    onChange={(e) => setTempTagName(e.target.value)}
                    placeholder="Enter tag name"
                    className="w-full px-2 py-2 bg-white border-[0.5px] border-[#D4D4D4] rounded-[8px] text-sm focus:outline-none focus:border-[#0BA6C7]"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSaveTag();
                      } else if (e.key === "Escape") {
                        setShowAddTagInput(false);
                        setTempTagName("");
                      }
                    }}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => {
                        setShowAddTagInput(false);
                        setTempTagName("");
                      }}
                      className="px-4 py-0.5 border-[0.5px] border-gray-300 text-gray-600 rounded-[8px] text-sm hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveTag}
                      disabled={!tempTagName.trim()}
                      className={`px-4 py-0.5 border-[0.5px] text-white rounded-[8px] text-sm transition-colors ${
                        tempTagName.trim()
                          ? "bg-[#0BA6C7] border-[#0BA6C7] hover:bg-[#0BA6C7]/90"
                          : "bg-[#808191] border-[#808191] cursor-not-allowed"
                      }`}
                    >
                      Create
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="px-4 pt-3">
              {availableTags.length === 0 ? (
                <div className="flex items-center gap-2 text-sm font-medium text-[#808191] cursor-not-allowed">
                  <Plus className="w-4 h-4" />
                  Add tag
                </div>
              ) : (
                !showAddTagInput && (
                  <button
                    onClick={handleAddNewTag}
                    disabled={availableTags.length >= 5}
                    className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                      availableTags.length >= 5
                        ? "text-[#808191] cursor-not-allowed"
                        : "text-[#0BA6C7] hover:text-[#0BA6C7]/80"
                    }`}
                  >
                    {availableTags.length < 5 ? (
                      <PlusColor className="w-4 h-4" />
                    ) : (
                      <Plus className="w-4 h-4" />
                    )}
                    Add tag
                    {availableTags.length >= 5 && (
                      <span className="text-xs">(Maximum reached)</span>
                    )}
                  </button>
                )
              )}
            </div>
          </ItemListPopup>
        </div>
      </div>

      {showDeletePopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="fixed inset-0 bg-black bg-opacity-50" />
          <div className="relative z-[61]">
            <DeletePopup
              title="Delete Tag"
              message={`Are you sure you want to permanently delete "${tagToDeleteName}"? This action cannot be undone.`}
              onCancel={handleCancelDelete}
              onConfirm={handleDeleteConfirmed}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default TagManagementPopup;
